import { MDTypography, MDBox } from '@components';
import CustomSlider from '@components/CustomSlider';
import { SELECT_BOTTOM_X, SELECT_TOP_X } from '@constants/eventNames';
import {
  Grid,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Switch
} from '@mui/material';
import { sendAmplitudeData } from '@utils/amplitude';
import { MarkType } from '.';
import { VisualSettings } from '@DStypes/chart';

interface ITopOrBottomSliderProps {
  topOrBottom: string;
  isDate: boolean | undefined;
  handleTopOrBottom: (value: boolean, field?: 'top' | 'bottom') => void;
  sliderValue: number;
  handleSliderChange: (value: number, topOrBottom: string) => void;
  sliderMarks: MarkType[];
  renderSliderLabel: (value: number) => string;
  isDisabled: boolean;
  visualSettings: VisualSettings | null;
  setVisualSettings: React.Dispatch<
    React.SetStateAction<VisualSettings | null>
  >;
  dataLength: number;
}

const TopOrBottomSlider = ({
  topOrBottom,
  isDate,
  handleTopOrBottom,
  sliderValue,
  handleSliderChange,
  sliderMarks,
  renderSliderLabel,
  isDisabled,
  visualSettings,
  setVisualSettings
}: ITopOrBottomSliderProps) => (
  <>
    <Grid container item xs={11} alignItems={'center'} minHeight='45px'>
      <Tooltip
        title={`${
          isDisabled
            ? 'Only available for Text columns'
            : visualSettings?.topOrBottomToggle
            ? 'Click to Remove Top/Bottom filter'
            : 'Click to Add Top/Bottom filter'
        }`}
        placement='top'
      >
        <span>
          <Switch
            checked={Boolean(
              !isDisabled && !!visualSettings?.topOrBottomToggle
            )}
            onChange={(e, value) => {
              setVisualSettings((prev) => ({
                ...prev,
                topOrBottomToggle: value
              }));
              if (!value) handleTopOrBottom(false); // Reset top/bottom slider if toggle is disabled
            }}
            disabled={isDisabled}
          />
        </span>
      </Tooltip>
      <MDTypography
        variant='sm'
        color='info'
        ml={1}
        mr={0.5}
        opacity={isDisabled ? 0.5 : 1}
      >
        Filter by Top / Bottom
      </MDTypography>
    </Grid>
    {!isDisabled && visualSettings?.topOrBottomToggle && (
      <Grid display='flex' alignItems='center'>
        <Grid container item xs={4} py={1} px={1}>
          <FormControlLabel
            value='top'
            control={<Checkbox />}
            label={
              <MDTypography variant='xs' color='info'>
                Top
              </MDTypography>
            }
            checked={topOrBottom === 'top'}
            disabled={isDisabled}
            onChange={(e, value) => {
              handleTopOrBottom(value, 'top');
              if (value) sendAmplitudeData(SELECT_TOP_X);
            }}
          />
          <FormControlLabel
            value='bottom'
            control={<Checkbox />}
            label={
              <MDTypography variant='xs' color='info'>
                Bottom
              </MDTypography>
            }
            checked={topOrBottom === 'bottom'}
            disabled={isDisabled}
            onChange={(e, value) => {
              handleTopOrBottom(value, 'bottom');
              if (value) sendAmplitudeData(SELECT_BOTTOM_X);
            }}
          />
        </Grid>

        <Grid item flex={1} mt={1} sx={{ paddingX: isDate ? '50px' : '10px' }}>
          <MDBox sx={{ textAlign: 'center' }}>
            <MDTypography id='slider' gutterBottom variant='xs' color='info'>
              {topOrBottom
                ? `${topOrBottom.toUpperCase()}: ${sliderValue}`
                : 'Selected: None'}
            </MDTypography>
          </MDBox>

          <Tooltip
            title={
              !topOrBottom
                ? 'Select Top or Bottom first, and then move the scale to show only Top or Bottom values (Ex: Top 5 or Bottom 10)'
                : ''
            }
            placement='top-start'
          >
            <MDBox>
              <CustomSlider
                value={sliderValue}
                onChange={(value: any) => {
                  handleSliderChange(value, topOrBottom);
                  sendAmplitudeData(
                    topOrBottom === 'top' ? SELECT_TOP_X : SELECT_BOTTOM_X
                  );
                }}
                min={sliderMarks[0].value}
                max={sliderMarks[1].value}
                getAriaValueText={(value: any) => renderSliderLabel(value)}
                getAriaLabel={(value: any) => renderSliderLabel(value)}
                marks={sliderMarks}
                disabled={!topOrBottom || isDisabled}
                topOrBottom={topOrBottom}
              />
            </MDBox>
          </Tooltip>
        </Grid>
      </Grid>
    )}
  </>
);

export default TopOrBottomSlider;
